"use client";

import { useTranslations } from "next-intl";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { WidgetPrechatField, WidgetPrechatForm } from "@/lib/widget-config";
import { autocompleteFor, inputTypeFor } from "./helpers";

interface PrechatComposerFieldsProps {
  form: WidgetPrechatForm;
  values: Record<string, string>;
  errors: Record<string, string>;
  onChange: (key: string, value: string) => void;
  /** Disable inputs while the combined send (prechat + message) is in flight. */
  disabled?: boolean;
  /** Field keys whose value comes from a verified source: shown read-only (still submitted). */
  locked?: ReadonlySet<string>;
}

/**
 * Compact prechat fields rendered ABOVE the message editor inside the
 * composer envelope — the visitor fills these once, alongside their
 * first message, and a single send submits both. Distinct from
 * `PrechatForm` (the full / standalone takeover); this variant is just
 * the field stack — no title, no own submit button, no `<form>`. The
 * parent (`MessagesTab`) owns the values, validation, and the combined
 * submit so the prechat write and the message send fire together.
 *
 * Kept deliberately small: the label lives in the placeholder (with a
 * persistent `aria-label` for screen readers), so each field is a
 * single tight row. Errors paint as a red ring + a one-line hint that
 * only takes space when present.
 */
export function PrechatComposerFields({
  form,
  values,
  errors,
  onChange,
  disabled = false,
  locked,
}: PrechatComposerFieldsProps) {
  const t = useTranslations("prechat");

  const labelFor = (field: WidgetPrechatField) => {
    if (field.label.trim()) return field.label;
    switch (field.kind) {
      case "NAME":
        return t("labelName");
      case "EMAIL":
        return t("labelEmail");
      case "PHONE":
        return t("labelPhone");
      case "CUSTOM_TEXT":
        return t("labelCustom");
    }
  };

  return (
    <div className="flex flex-col gap-1.5 border-wx-border border-b px-2.5 pt-2 pb-1.5">
      {form.fields.map((field) => {
        const label = labelFor(field);
        const err = errors[field.key];
        const isLocked = !!locked?.has(field.key);
        return (
          <div key={field.key}>
            <Input
              type={inputTypeFor(field.kind)}
              value={values[field.key] ?? ""}
              onChange={(e) => onChange(field.key, e.target.value)}
              autoComplete={autocompleteFor(field.kind)}
              disabled={disabled}
              readOnly={isLocked}
              aria-readonly={isLocked || undefined}
              data-locked={isLocked || undefined}
              title={isLocked ? t("locked") : undefined}
              aria-invalid={!!err}
              aria-label={label}
              placeholder={field.required ? `${label} *` : label}
              className={cn(
                "h-8 rounded-wx-md px-3 text-[13px]",
                isLocked && "cursor-default bg-wx-bg-elevated text-wx-fg-muted focus:ring-0",
                err && "ring-1 ring-wx-danger placeholder:text-wx-danger/60 focus:ring-wx-danger",
              )}
            />
            {err && <p className="mt-0.5 px-1 text-[10px] text-wx-danger leading-none">{err}</p>}
          </div>
        );
      })}
    </div>
  );
}
